import { formatPaise } from '@oneapp/gst';
import type { Invoice, ReturnDraft, ReturnIssue } from '@oneapp/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShortDate } from '../business/parts';
import { Badge, Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { type SalesFilter, salesApi, salesKeys } from './api';

/**
 * V1 · All sales: the month's sales, newest first, with what needs a look on top. Tapping a sale opens
 * it to change; a sale moved between tables says where it was.
 */

const KIND_TONE = { b2b: 'navy', b2cl: 'saffron', b2cs: 'gray' } as const;

function IssueCard({
  issue,
  busy,
  onResolve,
  onEdit,
}: {
  issue: ReturnIssue;
  busy: boolean;
  onResolve: (issue: ReturnIssue, action: ReturnIssue['actions'][number]) => void;
  onEdit: (invoiceId: string) => void;
}) {
  const { t } = useTranslation();
  // Removing a cancelled buyer's sale would drop real tax: moving it is the fix offered.
  const actions = issue.actions.filter((a) => a !== 'remove' || issue.kind !== 'buyer_cancelled');
  return (
    <section className="issue" aria-label={t('sales.issue.title', { number: issue.invoiceNumber ?? '' })}>
      <Callout tone="amber" icon="alert-triangle">
        <strong>{t('sales.issue.title', { number: issue.invoiceNumber ?? '' })}</strong> {issue.detail}
      </Callout>
      <div className="issue__actions">
        {actions.map((action) => (
          <Button
            key={action}
            small
            inline
            variant={action === 'move_to_b2c' ? undefined : 'secondary'}
            disabled={busy}
            onClick={() => onResolve(issue, action)}
          >
            {t(`sales.issue.${action}`)}
          </Button>
        ))}
        {issue.invoiceId ? (
          <Button small inline variant="ghost" onClick={() => onEdit(issue.invoiceId as string)}>
            {t('sales.issue.edit')}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/** Walk-in sales (Phase 16): one entry for the month, by its bills. */
function WalkInRow({ invoice, onOpen }: { invoice: Invoice; onOpen: (invoice: Invoice) => void }) {
  const { t } = useTranslation();
  const all = [...invoice.numbers, ...invoice.cancelled].sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
  const range = all.length > 1 ? `${all[0]}–${all[all.length - 1]}` : (all[0] ?? '');
  return (
    <li>
      <button type="button" className="sales__row" onClick={() => onOpen(invoice)}>
        <span className="sales__text">
          <span className="sales__number">{t('sales.list.walkIn')}</span>
          <span className="sales__meta">
            {t('sales.list.walkInMeta', { count: invoice.numbers.length, range })}
            {invoice.cancelled.length
              ? ` · ${t('sales.list.walkInCancelled', { count: invoice.cancelled.length })}`
              : ''}
          </span>
        </span>
        <span className="sales__amount">
          <span>{formatPaise(invoice.taxableValue)}</span>
          <Badge tone={KIND_TONE[invoice.kind]}>{t(`sales.list.kind.${invoice.kind}`)}</Badge>
        </span>
      </button>
    </li>
  );
}

/** A credit or debit note (gap 4): its invoice, and what it takes back (or adds). */
function NoteRow({ invoice, onOpen }: { invoice: Invoice; onOpen: (invoice: Invoice) => void }) {
  const { t } = useTranslation();
  const date = useShortDate();
  const credit = invoice.docType === 'credit';
  const on = invoice.against.map((a) => a.number).join(', ');
  return (
    <li>
      <button type="button" className="sales__row" onClick={() => onOpen(invoice)}>
        <span className="sales__text">
          <span className="sales__number">{invoice.numbers[0]}</span>
          <span className="sales__meta">
            {t('sales.list.noteOn', { number: on })} · {date(invoice.date)}
          </span>
        </span>
        <span className="sales__amount">
          <span>
            {credit ? '−' : '+'}
            {formatPaise(invoice.taxableValue)}
          </span>
          <Badge tone={credit ? 'amber' : 'navy'}>{t(credit ? 'sales.list.creditNote' : 'sales.list.debitNote')}</Badge>
        </span>
      </button>
    </li>
  );
}

function SaleRow({ invoice, onOpen }: { invoice: Invoice; onOpen: (invoice: Invoice) => void }) {
  const { t } = useTranslation();
  const date = useShortDate();
  if (invoice.source === 'walk_in') return <WalkInRow invoice={invoice} onOpen={onOpen} />;
  if (invoice.docType !== 'invoice') return <NoteRow invoice={invoice} onOpen={onOpen} />;
  const who = invoice.buyer ? (invoice.buyer.name ?? invoice.buyer.gstin) : t('sales.list.consumer');
  const more = invoice.numbers.length > 1 ? ` ${t('sales.list.more', { count: invoice.numbers.length - 1 })}` : '';
  return (
    <li>
      <button type="button" className="sales__row" onClick={() => onOpen(invoice)}>
        <span className="sales__text">
          <span className="sales__number">
            {invoice.numbers[0]}
            {more}
          </span>
          <span className="sales__meta">
            {who} · {date(invoice.date)}
            {invoice.amends ? (
              <em className="sales__note"> · {t('sales.list.corrected')}</em>
            ) : invoice.movedFrom ? (
              <em className="sales__note">
                {' '}
                · {t('sales.list.was', { kind: t(`sales.list.kind.${invoice.movedFrom}`) })}
              </em>
            ) : invoice.edited ? (
              <em className="sales__note"> · {t('sales.list.edited')}</em>
            ) : null}
          </span>
        </span>
        <span className="sales__amount">
          <span>{formatPaise(invoice.taxableValue)}</span>
          <Badge tone={KIND_TONE[invoice.kind]}>{t(`sales.list.kind.${invoice.kind}`)}</Badge>
        </span>
      </button>
    </li>
  );
}

export function SalesList({
  returnId,
  draft,
  periodName,
  onOpen,
  onAddNote,
  onCorrect,
  heading,
  summary,
  onResolved,
}: {
  returnId: string;
  draft: ReturnDraft;
  /** "August". */
  periodName: string;
  onOpen: (invoice: Invoice) => void;
  /** Gap 4: a credit or debit note. */
  onAddNote?: () => void;
  /** Gap 4b, a GSTR-1A: a filed sale to correct. */
  onCorrect?: () => void;
  /** Gap 4b, a GSTR-1A: its own title, and what it does to GSTR-3B in place of the totals. */
  heading?: string;
  summary?: string;
  onResolved: (draft: ReturnDraft) => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<SalesFilter>('all');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const list = useInfiniteQuery({
    queryKey: [...salesKeys.invoices(returnId, filter), draft.version],
    queryFn: ({ pageParam, signal }) => salesApi.invoices(returnId, filter, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const invoices = list.data?.pages.flatMap((page) => page.invoices) ?? [];
  const counts = draft.counts;
  const resolve = async (issue: ReturnIssue, action: ReturnIssue['actions'][number]) => {
    setBusy(true);
    setFailed(false);
    try {
      onResolved((await salesApi.resolve(returnId, issue.id, action)).return);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sales">
      <div className="sales__head">
        <h2 className="sheet__title">{heading ?? t('sales.list.title', { month: periodName })}</h2>
        <p className="sheet__text muted">
          {summary ??
            t('sales.list.summary', {
              count: counts.all,
              taxable: formatPaise(draft.totals.taxableValue),
              tax: formatPaise(draft.totals.tax),
            })}
        </p>
      </div>
      {failed ? (
        <Callout tone="error" icon="alert-triangle">
          {t('sales.list.resolveFailed')}
        </Callout>
      ) : null}
      {draft.issues.map((issue) => (
        <IssueCard
          key={issue.id}
          issue={issue}
          busy={busy}
          onResolve={resolve}
          onEdit={(invoiceId) => {
            const found = invoices.find((i) => i.id === invoiceId);
            if (found) onOpen(found);
          }}
        />
      ))}
      <fieldset className="filter-tabs" aria-label={t('sales.list.filter')}>
        {(counts.notes ? (['all', 'b2b', 'b2c', 'notes'] as const) : (['all', 'b2b', 'b2c'] as const)).map((id) => (
          <button
            key={id}
            type="button"
            className="filter-tabs__option"
            aria-pressed={filter === id}
            onClick={() => setFilter(id)}
          >
            {t(`sales.list.${id}`, { count: counts[id] })}
          </button>
        ))}
      </fieldset>
      {onAddNote || onCorrect ? (
        <div className="sales__more">
          {onCorrect ? (
            <Button variant="ghost" small inline icon="pencil" onClick={onCorrect}>
              {t('sales.list.correct')}
            </Button>
          ) : null}
          {onAddNote ? (
            <Button variant="ghost" small inline icon="undo" onClick={onAddNote}>
              {t('sales.list.addNote')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {list.isPending ? (
        <p className="muted">{t('sales.list.loading')}</p>
      ) : list.isError ? (
        <Callout tone="error" icon="alert-triangle">
          {t('sales.list.loadFailed')}
        </Callout>
      ) : invoices.length ? (
        <ul className="sales__list">
          {invoices.map((invoice) => (
            <SaleRow key={invoice.id} invoice={invoice} onOpen={onOpen} />
          ))}
        </ul>
      ) : (
        <p className="muted">{t('sales.list.empty')}</p>
      )}
      {list.hasNextPage ? (
        <Button
          variant="secondary"
          small
          inline
          disabled={list.isFetchingNextPage}
          onClick={() => list.fetchNextPage()}
        >
          {list.isFetchingNextPage ? t('sales.list.loadingMore') : t('sales.list.loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
